import { useEffect, useRef, useCallback } from "react"
import { useAuthStore } from "@/stores/authStore"
import { initFirebase, requestNotificationPermission, onForegroundMessage } from "@/lib/firebase"

interface NotificationPayload {
  title?: string
  body?: string
  data?: Record<string, string>
}

let registeredToken: string | null = null

export function useNotifications(onMessage?: (payload: NotificationPayload) => void) {
  const getAccessToken = useAuthStore((s) => s.getAccessToken)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  const registerToken = useCallback(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) return

    await initFirebase()
    const fcmToken = await requestNotificationPermission()
    if (!fcmToken || fcmToken === registeredToken) return

    try {
      const base = (import.meta.env.VITE_API_URL as string | undefined) ?? ""
      const root = base ? `${base.replace(/\/$/, "")}/v1/notifications` : "/v1/notifications"
      await fetch(`${root}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          token: fcmToken,
          platform: "web",
        }),
      })
      registeredToken = fcmToken
    } catch (err) {
      console.warn("Failed to register FCM token:", err)
    }
  }, [getAccessToken])

  useEffect(() => {
    if (!isAuthenticated) return
    registerToken()

    // Re-register hourly
    const interval = setInterval(registerToken, 60 * 60 * 1000)
    return () => clearInterval(interval)
  }, [isAuthenticated, registerToken])

  useEffect(() => {
    let unsub: (() => void) | null = null

    onForegroundMessage((payload) => {
      onMessageRef.current?.(payload)
    }).then((unsubscribe) => {
      unsub = unsubscribe
    })

    return () => unsub?.()
  }, [])
}
